"use strict";
/*
  assets/vat-display.js — site-wide VAT-inclusive display engine.

  Loaded globally (head, defer) from snippets/vat-bootstrap.liquid, after
  constants.js and pubsub.js so `subscribe` / `PUB_SUB_EVENTS` are available.

  Design rules that must not be broken:

  1. DISPLAY ONLY. Nothing here touches Shopify tax settings, cart contents or
     checkout. Every value is derived from the ex-VAT cents integer that Liquid
     stamps into `data-vat-cents`. Displayed text is NEVER parsed for a value —
     that is what caused compounding VAT in the old assets/vat.js.

  2. FORMAT-PRESERVING SUBSTITUTION. The server-rendered money string is
     snapshotted once per node into `data-vat-orig`; in the incl-VAT state only
     the numeric run inside that snapshot is replaced, reusing the separators
     already present. Currency symbol and position, ISO suffixes, "- " prefixes,
     "/ea" suffixes, "from" wrappers and translated sentences all survive.

  3. NEVER OBSERVE characterData OR attributes. layout/theme.liquid carries a
     global EUR reformatter whose observer DOES watch characterData. Writing a
     text node here is a characterData mutation; observing it back would make
     the two scripts ping-pong forever. The MutationObserver below is
     childList + subtree only.

  Public surface (also see snippets/vat-toggle.liquid):
    window.SPXVat.enabled / .rate / .available / .taxTerm
    window.SPXVat.set(bool, { silent: true })  .toggle()  .refresh()
    window.SPXVat.applyRate(cents) -> incl-VAT cents (for JS-driven widgets)
    document event "spx:vat:ready"   — fired once, after SPXVat exists
    document event "spx:vat:change"  — { detail: { enabled, rate, taxTerm } }
*/
(function () {
  if (window.SPXVat) return; // never initialise twice

  var CONFIG = window.SPXVatConfig || {};

  /* ------------------------------------------------------------------ *
   * 1. Rate table — the single source of truth.
   *    Mirrors the old assets/vat.js map plus LT (21) and SK (23), which
   *    were missing even though the theme localises PVM / DPH for them.
   *    Any country absent from this table resolves to 0 (engine disabled).
   * ------------------------------------------------------------------ */
  var VAT_RATES = {
    AT: 20, BE: 21, BG: 20, CY: 19, CZ: 21, DE: 19, DK: 25, EE: 24, ES: 21,
    FI: 25.5, FR: 20, GB: 20, GR: 24, HR: 25, HU: 27, IE: 23, IT: 22, LI: 8.1,
    LT: 21, LU: 17, LV: 21, MC: 20, MT: 18, NL: 21, NO: 25, PL: 23, PT: 23,
    RO: 19, SE: 25, SI: 22, SK: 23, TR: 20, AU: 10, CH: 8, CA: 5, US: 0
  };

  var STORAGE_KEY = "spx-vat-incl";
  var COUNTRY = String(CONFIG.country || "").toUpperCase();
  var RATE = Object.prototype.hasOwnProperty.call(VAT_RATES, COUNTRY) ? VAT_RATES[COUNTRY] : 0;
  var AVAILABLE = RATE > 0;
  var TAXES_INCLUDED = CONFIG.taxesIncluded === true;
  var TAX_TERM = CONFIG.taxTerm || "VAT";
  var CURRENCY_SYMBOL = CONFIG.currencySymbol || "";

  var TOGGLE_SELECTOR = "[data-spx-vat-toggle]";
  var NOTE_SELECTOR = "[data-spx-vat-note]";
  var PRICE_SELECTOR = "[data-vat-cents]";
  var WATCH_SELECTOR = PRICE_SELECTOR + "," + TOGGLE_SELECTOR + "," + NOTE_SELECTOR;

  /* One monetary run: digits, optionally broken by group/decimal separators,
     including the non-breaking spaces Shopify and Intl emit for fr/ru style
     grouping and the apostrophes de-CH uses ("CHF 2'999.00").
     Deliberately NOT global, so `test` and `exec` stay stateless. */
  var NUM_RE = /\d[\d.,'\u2019\u00a0\u202f ]*\d|\d/;

  /* Apostrophe group separator (de-CH / li). Never a decimal separator. */
  var APOSTROPHE_RE = /['\u2019]/;

  /* ------------------------------------------------------------------ *
   * 2. Persisted state (localStorage, with an in-memory fallback for
   *    Safari private mode / ITP where storage access throws).
   * ------------------------------------------------------------------ */
  var memoryValue = null;

  function readStored() {
    try {
      var value = window.localStorage.getItem(STORAGE_KEY);
      return value === null ? memoryValue : value;
    } catch (e) {
      return memoryValue;
    }
  }

  function writeStored(value) {
    memoryValue = value;
    try {
      window.localStorage.setItem(STORAGE_KEY, value);
    } catch (e) {
      /* storage unavailable — the in-memory value above keeps this page sane */
    }
  }

  function initialState() {
    var stored = readStored();
    if (stored === "1") return true;
    if (stored === "0") return false;
    return TAXES_INCLUDED; // false on this store, so the default is OFF
  }

  var enabled = AVAILABLE ? initialState() : false;

  /* ------------------------------------------------------------------ *
   * 3. Number handling
   * ------------------------------------------------------------------ */

  /* Fallback separators, used only when the snapshot string contains no
     separator at all (amounts under 1000, e.g. "€399"). Derived from the
     page language so an English page does not suddenly get "482,79". */
  var defaultConv = null;

  function defaultConvention() {
    if (defaultConv) return defaultConv;
    defaultConv = { dec: ",", grp: "." };
    try {
      var lang = (document.documentElement.lang || "").trim() || "en";
      var sample = new Intl.NumberFormat(lang).format(1234.5);
      var separators = sample.replace(/\d/g, "");
      var dec = separators.slice(-1);
      if (dec) {
        defaultConv = {
          dec: dec,
          grp: separators.slice(0, -1) || (dec === "," ? "." : ",")
        };
      }
    } catch (e) {
      /* keep the "1.234,56" fallback */
    }
    return defaultConv;
  }

  /* First text node inside `el` that holds a numeric run. */
  function findMoneyTextNode(el) {
    var walker;
    try {
      walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null, false);
    } catch (e) {
      return null;
    }
    var node;
    while ((node = walker.nextNode())) {
      if (node.nodeValue && NUM_RE.test(node.nodeValue)) return node;
    }
    return null;
  }

  /* Which characters this particular string uses for decimals and grouping.
     Mirrors layout/theme.liquid's toNumber() so the two agree. */
  function detectConvention(run) {
    var lastDot = run.lastIndexOf(".");
    var lastComma = run.lastIndexOf(",");
    var pos = Math.max(lastDot, lastComma);

    // de-CH style "2'999.00": the apostrophe is unambiguously the group
    // separator, so it wins over the language default and over the
    // dot/comma pairing rule below.
    var apostrophe = APOSTROPHE_RE.exec(run);
    if (apostrophe) {
      return { dec: pos === -1 ? "." : run.charAt(pos), grp: apostrophe[0] };
    }

    // fr/ru style "2 999,00": any space-family character between digits can
    // only be a group separator. Preserve it instead of regrouping with "."
    // (NUM_RE only admits these characters between digits, so a hit here is
    // always a separator, never surrounding whitespace).
    var space = /[\u00a0\u202f ]/.exec(run);
    if (space) {
      return { dec: pos === -1 ? defaultConvention().dec : run.charAt(pos), grp: space[0] };
    }

    if (pos === -1) return defaultConvention();

    var after = run.length - pos - 1;
    var single = lastDot === -1 || lastComma === -1;
    var separator = run.charAt(pos);

    // "2.895" / "2,895": three trailing digits and only one kind of separator
    // means it is a group separator, not a decimal point.
    if (after === 3 && single) {
      return { dec: separator === "." ? "," : ".", grp: separator };
    }
    return { dec: separator, grp: separator === "," ? "." : "," };
  }

  /* Render a major-unit value with the separators detected above. Always two
     decimals in the incl-VAT state (converted amounts virtually always have
     them). */
  function formatWith(valueMajor, conv) {
    var parts = Number(valueMajor).toFixed(2).split(".");
    var int = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, conv.grp);
    return int + conv.dec + parts[1];
  }

  /* ex-VAT cents -> displayed cents for the current state. Unrounded. */
  function convert(cents) {
    var factor = 1 + RATE / 100;
    if (TAXES_INCLUDED) {
      // Stored prices already include tax (not this store today, kept for parity)
      return enabled ? cents : cents / factor;
    }
    return enabled ? cents * factor : cents;
  }

  /* ------------------------------------------------------------------ *
   * 4. Core: convert one tagged node
   * ------------------------------------------------------------------ */
  function apply(el) {
    if (!AVAILABLE) return;
    if (el.hasAttribute("data-vat-skip")) return;

    var cents = parseInt(el.getAttribute("data-vat-cents"), 10);
    if (!isFinite(cents)) return;

    var node = findMoneyTextNode(el);
    if (!node) return; // e.g. an empty compare-at <s> — left completely alone

    if (el.dataset.vatOrig == null) el.dataset.vatOrig = node.nodeValue;

    var desired = enabled ? "incl" : "excl";
    if (el.dataset.vatState === desired) return; // idempotent short-circuit

    var orig = el.dataset.vatOrig;

    if (desired === "excl") {
      if (node.nodeValue !== orig) node.nodeValue = orig;
    } else {
      var match = NUM_RE.exec(orig);
      if (!match) return;
      // The match MUST span every digit in the snapshot. If a separator this
      // regex does not know about splits the amount, NUM_RE falls through to
      // its bare `\d` alternative and matches a single leading digit; splicing
      // that in would emit a mangled price ("CHF 3.238,92'999.00"). Bail out
      // instead and leave the server-rendered ex-VAT string standing.
      // data-vat-state is deliberately left unset so a later sweep retries.
      if (/\d/.test(orig.slice(0, match.index) + orig.slice(match.index + match[0].length))) {
        return;
      }
      var next =
        orig.slice(0, match.index) +
        formatWith(Math.round(convert(cents)) / 100, detectConvention(match[0])) +
        orig.slice(match.index + match[0].length);
      if (node.nodeValue !== next) node.nodeValue = next;
    }

    el.dataset.vatState = desired;
  }

  /* ------------------------------------------------------------------ *
   * 5. Toggles and notes
   * ------------------------------------------------------------------ */

  /* Rehydrates every toggle after a wholesale innerHTML replacement (cart
     drawer, #main-cart-footer), where the checkbox comes back at its Liquid
     default of unchecked and the wrapper comes back hidden. */
  function syncToggles() {
    var toggles = document.querySelectorAll(TOGGLE_SELECTOR);
    for (var i = 0; i < toggles.length; i++) {
      var toggle = toggles[i];
      if (toggle.hidden !== !AVAILABLE) toggle.hidden = !AVAILABLE;
      var input = toggle.querySelector('input[type="checkbox"]');
      // Only write when it differs, so no spurious change events are fired.
      if (input && input.checked !== enabled) input.checked = enabled;
    }
  }

  function rateLabel() {
    return String(RATE).replace(".", defaultConvention().dec) + "%";
  }

  function fillTemplate(template) {
    return String(template)
      .split("{rate}").join(rateLabel())
      .split("{term}").join(TAX_TERM);
  }

  function syncNotes() {
    var notes = document.querySelectorAll(NOTE_SELECTOR);
    for (var i = 0; i < notes.length; i++) {
      var note = notes[i];
      if (note.hidden !== !AVAILABLE) note.hidden = !AVAILABLE;
      if (!AVAILABLE) continue;
      var template = enabled ? note.dataset.noteIncl : note.dataset.noteExcl;
      if (template == null) continue;
      var text = fillTemplate(template);
      if (note.textContent !== text) note.textContent = text;
    }
  }

  /* ------------------------------------------------------------------ *
   * 6. Sweep — idempotent, cheap, rAF-coalesced
   * ------------------------------------------------------------------ */
  var applying = false;
  var scheduled = false;

  function sweep() {
    if (applying) return;
    applying = true;
    try {
      var nodes = document.querySelectorAll(PRICE_SELECTOR);
      for (var i = 0; i < nodes.length; i++) {
        // Per node, so one unusual node can never stop the rest of the sweep.
        try {
          apply(nodes[i]);
        } catch (e) {}
      }
      syncToggles();
      syncNotes();
    } catch (e) {
      /* never let the engine break the page */
    } finally {
      applying = false;
    }
  }

  /* At most one sweep per frame. The `scheduled` flag is flipped BEFORE the
     frame is requested, so the guard holds no matter when the callback runs. */
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    var run = function () {
      scheduled = false;
      sweep();
    };
    if (typeof requestAnimationFrame === "function") requestAnimationFrame(run);
    else setTimeout(run, 16);
  }

  /* ------------------------------------------------------------------ *
   * 7. Public state object
   * ------------------------------------------------------------------ */
  function dispatch(name, detail) {
    try {
      document.dispatchEvent(new CustomEvent(name, { detail: detail, bubbles: true }));
    } catch (e) {
      /* CustomEvent unsupported — nothing else depends on it */
    }
  }

  function setEnabled(value, opts) {
    opts = opts || {};
    if (!AVAILABLE) return; // rate 0: hard no-op, the engine writes nothing

    var next = !!value;
    if (next === enabled) {
      schedule(); // still re-sync, in case a toggle drifted out of step
      return;
    }

    enabled = next;
    if (!opts.skipStorage) writeStored(next ? "1" : "0");
    if (!opts.silent) {
      dispatch("spx:vat:change", { enabled: enabled, rate: RATE, taxTerm: TAX_TERM });
    }
    schedule();
  }

  var SPXVat = {
    country: COUNTRY,
    rate: RATE,
    taxTerm: TAX_TERM,
    taxesIncluded: TAXES_INCLUDED,
    currencySymbol: CURRENCY_SYMBOL,
    available: AVAILABLE,
    STORAGE_KEY: STORAGE_KEY,
    set: setEnabled,
    toggle: function () {
      setEnabled(!enabled);
    },
    refresh: function () {
      schedule();
    },
    /* For JS-driven widgets (gm2-addons, black-friday, premium-care-ribbon,
       calculator-eu) that compute their own sums: ex-VAT cents in, displayed
       cents out, using the shared rate and the current state. */
    applyRate: function (cents) {
      var value = Number(cents);
      if (!isFinite(value) || !AVAILABLE) return Math.round(value) || 0;
      return Math.round(convert(value));
    }
  };

  Object.defineProperty(SPXVat, "enabled", {
    get: function () {
      return enabled;
    },
    set: function (value) {
      setEnabled(value);
    },
    enumerable: true
  });

  window.SPXVat = SPXVat;
  dispatch("spx:vat:ready", { enabled: enabled, rate: RATE, taxTerm: TAX_TERM });

  /* ------------------------------------------------------------------ *
   * 8. <vat-calculator> — pure UI control (PDP).
   *    It no longer writes prices: the sweep above owns every money node,
   *    including the PDP ones, so there is exactly one writer per node.
   *    Identity contracts kept for assets/c-product-info.js: the tag name,
   *    #vat-{section.id} and #vat-checkbox-{section.id}.
   * ------------------------------------------------------------------ */
  if (typeof customElements !== "undefined" && !customElements.get("vat-calculator")) {
    class VatCalculator extends HTMLElement {
      constructor() {
        super();
        this.swatch = null;
        this.onChange = this.onChange.bind(this);
        this.onExternalChange = this.onExternalChange.bind(this);
      }

      connectedCallback() {
        this.swatch = this.querySelector('[data-component="swatch"]');

        // Reproduces the old vat.js rate-0 hide.
        if (!window.SPXVat || !window.SPXVat.available) {
          this.style.display = "none";
          return;
        }
        this.style.display = "";

        if (this.swatch) {
          this.swatch.checked = window.SPXVat.enabled;
          this.swatch.addEventListener("change", this.onChange);
        }
        document.addEventListener("spx:vat:change", this.onExternalChange);
      }

      disconnectedCallback() {
        if (this.swatch) this.swatch.removeEventListener("change", this.onChange);
        document.removeEventListener("spx:vat:change", this.onExternalChange);
      }

      onChange(event) {
        window.SPXVat.set(event.target.checked);
      }

      onExternalChange() {
        if (this.swatch) this.swatch.checked = window.SPXVat.enabled;
      }
    }

    customElements.define("vat-calculator", VatCalculator);
  }

  /* ------------------------------------------------------------------ *
   * 9. Triggers — everything funnels into schedule()
   * ------------------------------------------------------------------ */

  // (0) the user flipping any snippets/vat-toggle.liquid control.
  //     DELEGATED on purpose: the cart drawer (.drawer__inner) and the cart
  //     page (#main-cart-footer / .js-contents) are innerHTML-replaced
  //     wholesale on every cart update, so a listener bound to the checkbox
  //     itself would be thrown away — the same reason syncToggles() exists.
  //     syncToggles() only ever assigns `checked` when it differs, and a
  //     programmatic assignment fires no change event, so this cannot loop.
  //     The PDP's <vat-calculator> swatch is NOT inside [data-spx-vat-toggle]
  //     and keeps its own listener; it is not double-handled here.
  document.addEventListener("change", function (event) {
    var input = event.target;
    if (!input || input.type !== "checkbox" || typeof input.closest !== "function") return;
    if (!input.closest(TOGGLE_SELECTOR)) return;
    setEnabled(input.checked);
  });

  // (1) now, and again once the document is parsed
  schedule();
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", schedule);
  }

  // (2) our own state change
  document.addEventListener("spx:vat:change", schedule);

  // (3) product-info re-render (bubbles; c-product-info.js / product-info.js)
  document.addEventListener("product-info:loaded", schedule);

  // (4) + (5) theme pub/sub, if constants.js + pubsub.js loaded first
  if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
    try {
      if (PUB_SUB_EVENTS.variantChange) subscribe(PUB_SUB_EVENTS.variantChange, schedule);
      if (PUB_SUB_EVENTS.cartUpdate) subscribe(PUB_SUB_EVENTS.cartUpdate, schedule);
    } catch (e) {
      /* pub/sub not ready — the observer below still covers these paths */
    }
  }

  // (6) discount-form.js dispatches this on document
  document.addEventListener("cart:updated", schedule);

  // (7) theme editor section reload
  document.addEventListener("shopify:section:load", schedule);

  // (8) cross-tab sync
  window.addEventListener("storage", function (event) {
    if (!event || event.key !== STORAGE_KEY) return;
    var value = event.newValue;
    if (value !== "1" && value !== "0") return;
    // Do not write storage back: the other tab already owns the value.
    setEnabled(value === "1", { skipStorage: true });
    schedule();
  });

  // (9) DOM injection (cart drawer renderContents paints and publishes nothing).
  //     childList + subtree ONLY — never characterData, never attributes.
  if (typeof MutationObserver === "function") {
    try {
      new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          var added = mutations[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            var node = added[j];
            if (!node || node.nodeType !== 1) continue; // elements only
            if (
              (node.matches && node.matches(WATCH_SELECTOR)) ||
              (node.querySelector && node.querySelector(WATCH_SELECTOR))
            ) {
              schedule();
              return;
            }
          }
        }
      }).observe(document.documentElement, { childList: true, subtree: true });
    } catch (e) {
      /* observer unavailable — the timers below are the safety net */
    }
  }

  // (10) belt and braces, for app-embed injections that land after first paint
  [150, 600, 1500].forEach(function (delay) {
    setTimeout(schedule, delay);
  });
})();
